import React, { forwardRef, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FixedSizeList as List } from 'react-window';
import { Skeleton, useTheme } from '@mui/material';
import { HotelPropertyFareCard } from '../HotelPropertyFareCard';
import { HotelFareOption } from '@bandido/shared/types';
import { useGetHotelNimbuxHotelById } from '@frontend/features/hotel-content-manager/queries';
import { useTranslation } from 'react-i18next';
import { InfoLanguage } from '@bandido/shared/enums';

interface HotelPropertyFaresListProps {
  loading: boolean;
  fares?: HotelFareOption[];
}

const ScrollContainer = forwardRef<HTMLDivElement, React.HTMLProps<HTMLDivElement>>(
  ({ children, ...props }, ref) => {
    useEffect(() => {
      const styleId = 'hotel-property-fares-scrollbar-styles';
      if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
          .hotel-property-fares-scrollbar::-webkit-scrollbar {
            width: 8px;
          }
          .hotel-property-fares-scrollbar::-webkit-scrollbar-track {
            background: transparent;
          }
          .hotel-property-fares-scrollbar::-webkit-scrollbar-thumb {
            background: rgba(74, 20, 140, 0.5);
            border-radius: 4px;
            transition: background-color 0.2s ease;
          }
          .hotel-property-fares-scrollbar::-webkit-scrollbar-thumb:hover {
            background: rgba(74, 20, 140, 0.8);
          }
          .hotel-property-fares-scrollbar::-webkit-scrollbar-thumb:active {
            background: rgba(74, 20, 140, 1);
          }
        `;
        document.head.appendChild(style);
      }
    }, []);

    return (
      <div
        ref={ref}
        {...props}
        className="hotel-property-fares-scrollbar"
        style={{
          ...props.style,
          scrollbarWidth: 'thin' as const,
          scrollbarColor: 'rgba(74, 20, 140, 0.5) transparent',
        }}
      >
        {children}
      </div>
    );
  }
);

export const HotelPropertyFaresList: React.FC<HotelPropertyFaresListProps> = ({
  loading,
  fares,
}) => {
  const itemCount = loading ? 5 : fares?.length ?? 0;
  const theme = useTheme();
  const { hotelId } = useParams<{ hotelId: string }>();
  const { data: nimbuxHotel } = useGetHotelNimbuxHotelById(Number(hotelId));
  const { i18n } = useTranslation();
  const [calculatedHeight, setCalculatedHeight] = useState<number>(600);

  useEffect(() => {
    const calculateHeight = () => {
      const viewportHeight = window.innerHeight;
      const offsetHeight = 140;
      const newHeight = viewportHeight - offsetHeight;
      setCalculatedHeight(Math.max(300, newHeight));
    };

    const handleResize = () => {
      calculateHeight();
    };

    window.addEventListener('resize', handleResize);
    calculateHeight();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const getBoardDescription = (boardDescription: string) => {
    try {
      const boardDescriptionParsed = JSON.parse(boardDescription);
      return boardDescriptionParsed[i18n.language as InfoLanguage];
    } catch (error) {
      return boardDescription;
    }
  };

  const renderItem = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    if (loading) {
      return (
        <div style={{ ...style, boxSizing: 'border-box' }}>
          <div style={{ height: '310px' }}>
            <Skeleton height="100%" variant="rounded" animation="wave" />
          </div>
        </div>
      );
    }
    if (fares?.length && fares.length > 0) {
      const fare = fares?.[index];
      return (
        <div style={{ 
            ...style, 
            paddingRight: theme.spacing(2), 
            boxSizing: 'border-box', 
            display: 'flex',
            alignItems: 'center',
            height: '276px' ,
            marginBottom: '12px',
          }}>
          <HotelPropertyFareCard
            propertyName={nimbuxHotel?.name ?? ''}
            rooms={fare?.rooms}
            boardDescription={getBoardDescription(fare?.boardDescription ?? '')}
            rateRules={fare?.rateRules ?? []}
            cancelPolicy={{
              refundable: true,
              description: 'Refundable'
            }}
            supplierName={fare?.supplierName}
            currency={fare?.price.currency}
            checkinDate={fare?.checkIn}
            checkoutDate={fare?.checkOut}
            price={fare?.price.net}
            onSelectFare={() => console.log(`Selected fare ${fare.id}`)}
          />
        </div>
      );
    }
  };

  if (itemCount <= 3) {
    return (
      <>
        {Array.from({ length: itemCount }).map((_, index) => (
          <div
            key={loading ? `skeleton-${index}` : fares?.[index]?.id}
            style={{ 
              padding: theme.spacing(1), 
              boxSizing: 'border-box',
              marginBottom: '12px'
            }}
          >
            {loading ? (
              <div style={{ height: '276px' }}>
                <Skeleton height="100%" variant="rounded" animation="wave" />
              </div>
            ) : (
              <HotelPropertyFareCard
                propertyName={nimbuxHotel?.name ?? ''}
                rooms={fares?.[index]?.rooms ?? []}
                boardDescription={getBoardDescription(fares?.[index]?.boardDescription ?? '')}
                checkinDate={fares?.[index].checkIn!}
                checkoutDate={fares?.[index].checkOut!}
                rateRules={fares?.[index].rateRules ?? []}
                cancelPolicy={fares?.[index].cancelPolicy ?? {
                  refundable: false,
                  description: 'Non-refundable'
                }}
                supplierName={fares?.[index].supplierName!}
                currency={fares?.[index].price.currency!}
                price={fares?.[index].price.net!}
                onSelectFare={() => console.log(`Selected fare ${fares?.[index].id}`)}
              />
            )}
          </div>
        ))}
      </>
    );
  }

  return (
    <List
      height={calculatedHeight}
      width="100%"
      itemCount={itemCount}
      itemSize={340}
      overscanCount={10}
      outerElementType={ScrollContainer}
    >
      {renderItem}
    </List>
  );
};
